import { useCallback, useEffect, useRef, useState } from 'react'
import type { AnalyticsEnvelope } from './alertTypes'

export type AnalyticsStatus = 'idle' | 'loading' | 'ok' | 'error'

export const ALERT_ANALYTICS_BASE = '/api/reports/alerts'

/** Give up on one request after this long. The reporting DB's own statement_timeout is
 *  15 s, so a healthy server always answers well inside it; past it the request is hung
 *  (backend down behind a proxy that never fails fast), and a report stuck on "Reading
 *  the alert record…" forever reads as broken rather than as "the server is not answering".
 *  One-shot per request -- not a retry loop. */
export const ALERT_ANALYTICS_TIMEOUT_MS = 30_000
export const ALERT_ANALYTICS_TIMEOUT_MESSAGE =
  'The reporting server did not answer within 30 s. Try again in a moment, or narrow the window.'

/** One GET against /api/reports/alerts{path}. Throws with the server's own `error`
 *  (+ `hint`) so the view can show the real reason -- "reporting database not configured"
 *  reads very differently from a 403 on the site gate. */
export async function fetchAlertAnalytics<E>(
  path: string,
  params: URLSearchParams | null,
  signal?: AbortSignal,
): Promise<E> {
  const qs = params?.toString() ?? ''
  const r = await fetch(`${ALERT_ANALYTICS_BASE}${path}${qs ? `?${qs}` : ''}`, { signal, credentials: 'same-origin' })
  const body = (await r.json().catch(() => null)) as (Record<string, unknown> & { error?: string; hint?: string }) | null
  if (!r.ok || body === null || typeof body.error === 'string') {
    const msg = body?.error ?? `${path.replace(/^\//, '')}: HTTP ${r.status}`
    throw new Error(body?.hint ? `${msg} — ${body.hint}` : msg)
  }
  return body as unknown as E
}

export interface UseAlertAnalyticsResult<T, E> {
  rows: T[]
  envelope: E | null
  status: AnalyticsStatus
  error: string | null
  refetch: () => void
}

/**
 * Fetch-on-demand hook for the alert analytics endpoints. Exactly ONE request per
 * distinct (path, params) -- the params' string form is the dependency, so two
 * URLSearchParams with the same content do not refetch -- and the in-flight request is
 * aborted the moment they change, so a fast filter edit never lets a stale answer land
 * on top of a newer one.
 *
 * Deliberately NO cache (`useReportData`'s cache key ignores the date range, which is
 * wrong for every call here) and NO interval: CLAUDE.md forbids new polling loops under
 * console/src, and a report re-reads when its inputs change or someone asks, not on a
 * timer. `deps` are extra refetch triggers for exactly that (e.g. the SSE-driven triage
 * map), and `params === null` means "not ready" -> idle, no request.
 */
export function useAlertAnalytics<T, E extends AnalyticsEnvelope<T> = AnalyticsEnvelope<T>>(
  path: string,
  params: URLSearchParams | null,
  deps: readonly unknown[] = [],
): UseAlertAnalyticsResult<T, E> {
  const [rows, setRows] = useState<T[]>([])
  const [envelope, setEnvelope] = useState<E | null>(null)
  const [status, setStatus] = useState<AnalyticsStatus>(params ? 'loading' : 'idle')
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const refetch = useCallback(() => setTick((t) => t + 1), [])

  const key = params ? params.toString() : null
  // The latest params object, read inside the effect so the effect's own dependency can
  // stay the string key (object identity would refetch on every render).
  const paramsRef = useRef(params)
  paramsRef.current = params

  useEffect(() => {
    if (key === null) {
      setStatus('idle')
      return
    }
    const ac = new AbortController()
    let timedOut = false
    const timer = setTimeout(() => { timedOut = true; ac.abort() }, ALERT_ANALYTICS_TIMEOUT_MS)
    setStatus('loading')
    setError(null)
    fetchAlertAnalytics<E>(path, paramsRef.current, ac.signal)
      .then((env) => {
        clearTimeout(timer)
        if (ac.signal.aborted) return
        setEnvelope(env)
        setRows(Array.isArray(env.rows) ? env.rows : [])
        setStatus('ok')
      })
      .catch((e: unknown) => {
        clearTimeout(timer)
        if (timedOut) {
          setError(ALERT_ANALYTICS_TIMEOUT_MESSAGE)
          setRows([])
          setEnvelope(null)
          setStatus('error')
          return
        }
        if (ac.signal.aborted) return
        // A fetch rejected by our own abort surfaces as DOMException 'AbortError' in some
        // runtimes before the aborted flag is observable -- never report it as an error.
        if (e instanceof DOMException && e.name === 'AbortError') return
        setError(e instanceof Error ? e.message : String(e))
        setRows([])
        setEnvelope(null)
        setStatus('error')
      })
    return () => {
      clearTimeout(timer)
      ac.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, tick, ...deps])

  return { rows, envelope, status, error, refetch }
}
